import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import api from '../services/api';
import useAuthStore from '../context/authStore';
import { GRADES } from './DashboardPage';
import './AuthPages.css';

function AuthPages({ mode }) {
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref');
  const redirectPath = searchParams.get('redirect');
  const defaultRole = searchParams.get('role') || 'student';
  const [role, setRole] = useState(defaultRole);
  const [loading, setLoading] = useState(false);
  const [authMode, setAuthMode] = useState(mode); // 'login', 'register', 'forgot'
  const { signIn, signUp, signInWithGoogle, resetPassword } = useAuthStore();
  const navigate = useNavigate();
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const [codeStatus, setCodeStatus] = useState(null);
  const watchedCode = watch('referredBy');
  const watchedUsername = watch('username');
  const [userStatus, setUserStatus] = useState(null);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (role === 'teacher' && watchedUsername) {
        try {
          const { data } = await api.get(`/users/check-username/${watchedUsername}`);
          setUserStatus(data);
        } catch { setUserStatus(null); }
      } else { setUserStatus(null); }

      const code = watchedCode || referralCode;
      if (code && code.length >= 3) {
        try {
          const { data } = await api.get(`/users/check-code/${code}`);
          setCodeStatus(data);
        } catch { setCodeStatus(null); }
      } else {
        setCodeStatus(null);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [watchedCode, referralCode]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (authMode === 'login') {
        await signIn(data.email, data.password);
        toast.success('أهلاً بيك في مدرسك!');
        if (redirectPath) navigate(redirectPath);
        else navigate(role === 'admin' ? '/admin' : '/dashboard');
      } else if (authMode === 'register') {
        await signUp(data.email, data.password, data.name, role, {
          bio: data.bio || '',
          title: data.title || '',
          subjects: data.subjects ? data.subjects.split(',').map(s => s.trim()) : [],
          phone: data.phone || '',
          parentPhone: data.parentPhone || '',
          grade: data.grade || '',
          username: data.username,
          referredBy: data.referredBy || referralCode || '',
        });
        toast.success('تم إنشاء حسابك بنجاح!');
        if (redirectPath) navigate(redirectPath);
        else navigate(role === 'admin' ? '/admin' : '/dashboard');
      } else if (authMode === 'forgot') {
        await resetPassword(data.email);
        toast.success('تم إرسال رابط إعادة تعيين كلمة المرور لبريدك من madrasak.eg@gmail.com');
        setAuthMode('login');
      }
    } catch (err) {
      toast.error(err.message || 'حدث خطأ، حاول تاني');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setLoading(true);
    try {
      await signInWithGoogle(role);
      toast.success('تم تسجيل الدخول بنجاح!');
      if (redirectPath) navigate(redirectPath);
      else navigate('/dashboard');
    } catch (err) {
      toast.error(err.message || 'حدث خطأ');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <Link to="/" className="auth-logo">مدرسك</Link>
        <h2 className="auth-title">
          {authMode === 'login' ? 'تسجيل الدخول' : authMode === 'register' ? 'إنشاء حساب جديد' : 'نسيت كلمة المرور'}
        </h2>
        <p className="auth-sub">
          {authMode === 'login' ? 'مرحباً بعودتك! سجّل دخولك للمتابعة' :
           authMode === 'register' ? 'انضم لآلاف المدرسين والطلاب على مدرسك' :
           'دخل بريدك الإلكتروني وهنبعتلك رابط تغير كلمة المرور'}
        </p>

        {authMode === 'register' && (
          <div className="role-toggle">
            <button className={`role-btn ${role === 'student' ? 'active' : ''}`} onClick={() => setRole('student')}>
              👨‍🎓 طالب
            </button>
            <button className={`role-btn ${role === 'teacher' ? 'active' : ''}`} onClick={() => setRole('teacher')}>
              👨‍🏫 مدرّس
            </button>
          </div>
        )}

        {authMode !== 'forgot' && (
          <>
            <button className="google-btn" onClick={handleGoogle} disabled={loading}>
              <span style={{ fontWeight: 800, color: '#4285F4' }}>G</span>
              {authMode === 'login' ? 'تسجيل الدخول بـ Google' : 'إنشاء حساب بـ Google'}
            </button>
            <div className="auth-divider"><span>أو</span></div>
          </>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          {authMode === 'register' && (
            <div className="form-group">
              <label className="form-label">الاسم الكامل</label>
              <input className={`form-input ${errors.name ? 'error' : ''}`} placeholder="محمد أحمد" {...register('name', { required: 'الاسم مطلوب' })} />
              {errors.name && <span className="form-error">{errors.name.message}</span>}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">البريد الإلكتروني</label>
            <input type="email" className={`form-input ${errors.email ? 'error' : ''}`} placeholder="example@email.com" {...register('email', { required: 'البريد الإلكتروني مطلوب' })} />
            {errors.email && <span className="form-error">{errors.email.message}</span>}
          </div>

          {authMode === 'register' && (
            <div className="form-group">
              <label className="form-label">رقم الهاتف</label>
              <input className={`form-input ${errors.phone ? 'error' : ''}`} placeholder="010XXXXXXXX" {...register('phone', { required: 'رقم الهاتف مطلوب' })} />
              {errors.phone && <span className="form-error">{errors.phone.message}</span>}
            </div>
          )}

          {authMode === 'register' && role === 'student' && (
            <>
              <div className="form-group">
                <label className="form-label">رقم هاتف ولي الأمر</label>
                <input className={`form-input ${errors.parentPhone ? 'error' : ''}`} placeholder="010XXXXXXXX" {...register('parentPhone', { required: 'رقم هاتف ولي الأمر مطلوب' })} />
                {errors.parentPhone && <span className="form-error">{errors.parentPhone.message}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">الصف الدراسي</label>
                <select className={`form-input ${errors.grade ? 'error' : ''}`} {...register('grade', { required: 'الصف الدراسي مطلوب' })}>
                  <option value="">-- اختر الصف --</option>
                  {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
                {errors.grade && <span className="form-error">{errors.grade.message}</span>}
              </div>
            </>
          )}

          {authMode !== 'forgot' && (
            <div className="form-group">
              <label className="form-label">كلمة المرور</label>
              <input type="password" className={`form-input ${errors.password ? 'error' : ''}`} placeholder="••••••••" {...register('password', { required: 'كلمة المرور مطلوبة', minLength: { value: 6, message: 'على الأقل 6 أحرف' } })} />
              {errors.password && <span className="form-error">{errors.password.message}</span>}
            </div>
          )}

          {authMode === 'login' && (
            <div style={{ textAlign: 'left', marginBottom: 20, marginTop: -10 }}>
              <button type="button" className="auth-link-btn" onClick={() => setAuthMode('forgot')}>نسيت كلمة المرور؟</button>
            </div>
          )}

          {authMode === 'register' && (
            <div className="form-group">
              <label className="form-label">كود الإحالة أو الخصم (اختياري)</label>
              <input
                className="form-input"
                placeholder="ادخل الكود هنا للحصول على هدية 🎁"
                defaultValue={referralCode || ''}
                {...register('referredBy')}
              />
              {codeStatus && (
                <p style={{ fontSize: 11, color: codeStatus.valid ? 'var(--success)' : 'var(--danger)', marginTop: 4, fontWeight: codeStatus.valid ? 700 : 400 }}>
                  {codeStatus.message}
                </p>
              )}
              {!codeStatus && (
                <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  استخدم كود صديق أو كود إعلان للحصول على رصيد مجاني عند التسجيل.
                </p>
              )}
            </div>
          )}

          {authMode === 'register' && role === 'teacher' && (
            <>
              <div className="form-group">
                <label className="form-label">اسم المستخدم المميز (Username) *</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, direction: 'ltr' }}>
                  <span style={{ color: 'var(--text-muted)' }}>madrasak.eg/teachers/</span>
                  <input
                    className="form-input"
                    placeholder="username"
                    {...register('username', {
                      required: role === 'teacher',
                      pattern: { value: /^[a-z0-9]+$/, message: 'أحرف إنجليزية وأرقام فقط' }
                    })}
                  />
                </div>
                {userStatus && (
                  <p style={{ fontSize: 11, color: userStatus.available ? 'var(--success)' : 'var(--danger)', marginTop: 4 }}>
                    {userStatus.available ? '✅ اسم المستخدم متاح' : '❌ اسم المستخدم مستخدم بالفعل'}
                  </p>
                )}
                <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, textAlign: 'right' }}>هذا سيكون رابط بروفايلك الذي تشاركه مع طلابك.</p>
              </div>
              <div className="form-group">
                <label className="form-label">المسمى الوظيفي (مثلاً: خبير أول فيزياء)</label>
                <input className="form-input" placeholder="المسمى الوظيفي..." {...register('title')} />
              </div>
              <div className="form-group">
                <label className="form-label">نبذة عنك</label>
                <textarea className="form-input" rows={3} placeholder="اكتب نبذة مختصرة عنك وعن تخصصك..." {...register('bio')} />
              </div>
              <div className="form-group">
                <label className="form-label">المواد التي تدرّسها (افصل بفاصلة)</label>
                <input className="form-input" placeholder="رياضيات, فيزياء, كيمياء" {...register('subjects')} />
              </div>
              <div className="teacher-note">
                <span>⚠️</span>
                <p>حسابات المدرسين تحتاج موافقة الإدارة قبل نشر الكورسات. هيتواصلوا معاك خلال 24 ساعة.</p>
              </div>
            </>
          )}

          <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '12px' }} disabled={loading}>
            {loading ? <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} /> : (authMode === 'login' ? 'تسجيل الدخول' : authMode === 'register' ? 'إنشاء الحساب' : 'إرسال رابط الاستعادة')}
          </button>
        </form>

        <p className="auth-switch">
          {authMode === 'login' ? (
            <>مش عندك حساب؟ <Link to="/register" onClick={() => setAuthMode('register')}>سجّل الآن</Link></>
          ) : authMode === 'register' ? (
            <>عندك حساب؟ <Link to="/login" onClick={() => setAuthMode('login')}>سجّل دخولك</Link></>
          ) : (
            <button type="button" className="auth-link-btn" onClick={() => setAuthMode('login')}>← العودة لتسجيل الدخول</button>
          )}
        </p>
      </div>
    </div>
  );
}

export default AuthPages;
